import React from 'react';
import {
  Users, ChevronDown, ChartLine, Syringe, ClipboardPlus, User, FileText, Activity,
  KeyRound, Clock, Calendar, CalendarDays, Receipt, PieChart, Settings, Building,
  LogOut, X, XCircle, Menu, CreditCard,
  Home, Stethoscope, UserCheck, History, Send, ClipboardList, NotebookTabs, Shield, TrendingUp,
  ChevronsLeft, ChevronsRight, ClipboardSignature, FolderOpen, CalendarCheck, HeartHandshake, BellRing, ClipboardCheck
} from 'lucide-react';
import iconWhite from '../../assets/images/icon_white.png';
import { usePatient } from '../../context/patientContext';
import UserProfileBadge from '../common/UserProfileBadge';
import PatientAvatar from '../common/PatientAvatar';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useHasAnyPermission } from '../../hooks/usePermission';
import type { PermissionString } from '../../types/permissions';
import { useFeature } from '../../hooks/useFeature';
import SupportLinks from './SupportLinks';

// Menu styles
const menuStyles = {
  mainMenuItem: (isCollapsed: boolean, isActive: boolean) => `
    ${isCollapsed ? 'justify-center flex-col items-center' : ''} 
    py-2 px-3 rounded-lg flex items-center text-gray-200 hover:bg-slate-700 cursor-pointer
    ${isActive ? (isCollapsed ? 'bg-slate-700' : 'bg-slate-700') : ''}
  `,
  subMenuItem: (isCollapsed: boolean, isActive: boolean, isMobile: boolean) => `
    ${isCollapsed ? 'justify-center flex-col items-center' : ''} 
    py-${isMobile ? '3' : '2'} px-3 rounded-lg flex items-center text-gray-200 hover:bg-slate-700 cursor-pointer 
    ${isActive ? (isCollapsed ? 'bg-slate-700' : 'bg-slate-700') : ''}
  `,
  categoryHeader: (isCollapsed: boolean, isExpanded: boolean) => `
    flex items-center justify-between cursor-pointer py-2 px-2 rounded-lg
    ${isCollapsed ? 'justify-center' : ''}
    text-gray-200 hover:bg-slate-700
  `,
  categorySubItem: (isCollapsed: boolean, isActive: boolean, isMobile: boolean, indent: number = 0) => `
    ${isCollapsed ? 'justify-center flex-col items-center' : `ml-${4 + indent * 2}`} 
    py-${isMobile ? '2' : '1.5'} px-3 rounded-lg flex items-center text-gray-300 hover:bg-slate-700 cursor-pointer text-sm
    ${isActive ? (isCollapsed ? 'bg-slate-700' : 'bg-slate-700') : ''}
  `,
  iconStyle: (isCollapsed: boolean, isActive: boolean) => `
    ${isCollapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : 'text-gray-300'}
  `,
  sectionHeader: (isCollapsed: boolean) => `
    flex items-center justify-between cursor-pointer 
    ${isCollapsed ? 'justify-center' : ''}
  `,
  submenuContainer: (isCollapsed: boolean, isMobile: boolean) => `
    ${isCollapsed ? 'mt-2 px-0 flex flex-col items-center' : 'mt-2 pl-7'} 
    space-y-${isMobile ? '2' : '1'}
  `
};

interface Patient {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  dob: string;
  mrn: string;
  status?: string;
  initials?: string;
  profilePictureUrl?: string | null;
}

interface SidebarProps {
  isMobile: boolean;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
  myPatientsExpanded: boolean;
  setMyPatientsExpanded: (expanded: boolean) => void;
  mockPatients?: Patient[];
  myPatients?: Patient[];
  activeSection: string;
  activeSubsection: string | null;
  activeSidebarSection?: string;
  activeAdminTab: string | null;
  setActiveSection: (section: string) => void;
  setActiveSubsection: (subsection: string | null) => void;
  setActiveAdminTab: (tab: string | null) => void;
  scrollToSection: (section: string) => void;
  handleSignOut: () => void;
  userId: string;
  firstName: string;
  lastName: string;
  currentUserEmail: string;
  userProfilePic: string | null;
  onProfilePictureUpdate?: (imageUrl: string) => void;
  patientSearchQuery: string;
  setPatientSearchQuery: (query: string) => void;
  handlePatientSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  patientSearchInputRef: React.RefObject<HTMLDivElement>;
  patientSearchResultsRef: React.RefObject<HTMLDivElement>;
  myPatientsLoading?: boolean;
  sidebarPinned: boolean;
  toggleSidebarPin: () => void;
  sidebarHovered: boolean;
  setSidebarHovered: (v: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isMobile,
  sidebarCollapsed,
  toggleSidebar,
  expandedSections,
  toggleSection,
  myPatientsExpanded,
  setMyPatientsExpanded,
  mockPatients = [],
  myPatients = [],
  activeSection,
  activeSubsection,
  activeSidebarSection,
  activeAdminTab,
  setActiveSection,
  setActiveSubsection,
  setActiveAdminTab,
  scrollToSection,
  handleSignOut,
  userId,
  firstName,
  lastName,
  myPatientsLoading = false,
  currentUserEmail,
  userProfilePic,
  onProfilePictureUpdate,
  patientSearchQuery,
  setPatientSearchQuery,
  handlePatientSearch,
  patientSearchInputRef,
  patientSearchResultsRef,
  sidebarPinned,
  toggleSidebarPin,
  sidebarHovered,
  setSidebarHovered
}) => {
  // Use PatientContext for selection state
  const { selectedPatient, setSelectedPatient, selectedPatientId, setSelectedPatientId, setPatientUnselected } = usePatient();

  // Check if user has permission to read agencies (view Agency Setup)
  const canReadAgencies = useCanPerform('read:agencies');

  // Check if user has permission to read system config
  const canReadConfig = useCanPerform('read:config');
  const canManageNotifications = useCanPerform('manage:notificationsettings');

  // Check if user has permission to read patients (view Patients)
  const canReadPatients = useCanPerform('read:patients:nonphi');

  // Check if user has permission to read users (view Users)
  const canReadUsers = useCanPerform('read:users:pii');

  // Check if user has permission to read billing/claims
  const canReadBilling = useCanPerform('read:claims');
  // PRD §3.2: EVV reconciliation is Admin, Coordinator, Billing, Read-only (Phase 10)
  const canReadEvv = useHasAnyPermission(['evv:reconciliation:read' as PermissionString]);

  // Phase 3 feature flags (default OFF: hidden)
  const oasisEnabled = useFeature('oasis');
  const billingReportsEnabled = useFeature('billingReports');

  // Check if user has permission to read care plans
  const canReadCareplan = useHasAnyPermission([
    'patientmr:phi:careplan:read' as any,
    'patientmr:phi:careplan:readall' as any
  ]);

  // Check if user has permission to read assessments
  const canReadAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any
  ]);

  // Check if user has permission to read charting
  const canReadCharting = useHasAnyPermission([
    'patientmr:phi:charting:read' as any,
    'patientmr:phi:charting:readall' as any
  ]);

  // Check if user has permission to read referrals
  const canReadReferral = useHasAnyPermission([
    'patientmr:phi:referral:read' as any,
    'patientmr:phi:referral:readall' as any
  ]);

  // Check if user has permission to read insurance
  const canReadInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:read' as any,
    'patientmr:phi:insurance:readall' as any
  ]);

  // Check if user has permission to read authorizations
  const canReadAuthorization = useHasAnyPermission([
    'patientmr:phi:authorization:read' as any,
    'patientmr:phi:authorization:readall' as any
  ]);

  // Phase 12 (§4.4): the real form read check (was hard-coded true)
  const canReadForm = useHasAnyPermission([
    'patientmr:phi:form:read' as PermissionString,
    'patientmr:phi:form:readall' as PermissionString
  ]);
  // Phase 12: staff Family Portal tab (invites, approval, revoke)
  const canReadFamilyPortal = useHasAnyPermission(['admin:patient:familyportal:read' as PermissionString]);

  // Check if user has permission to read demographics
  const canReadDemographics = useHasAnyPermission([
    'patientmr:phi:demographics:read' as any,
    'patientmr:phi:demographics:readall' as any
  ]);

  // Check if user has permission to read medications
  const canReadMedications = useHasAnyPermission([
    'patientmr:phi:medications:read' as any,
    'patientmr:phi:medications:readall' as any
  ]);

  // Check if user has permission to read Care Team
  const canReadProviders = useHasAnyPermission([
    'patientmr:phi:providers:read' as any,
    'patientmr:phi:providers:readall' as any
  ]);

  // Check if user has permission to read Growth Tracking
  const canReadGrowthChart = useHasAnyPermission([
    'patientmr:phi:growthchart:read' as any,
    'patientmr:phi:growthchart:readall' as any
  ]);

  // Check if user has permission to read Dashboard
  const canReadDashboard = useHasAnyPermission([
    'patientmr:phi:dashboard:read' as any
  ]);

  // Phase 8 (§3.8): POC / CMS-485 and Orders under Clinical Care (read:poc)
  const canReadPoc = useHasAnyPermission([
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any
  ]);

  // Check if user has permission to read Clinical Care (at least one of the modules)
  const canReadClinicalCare = useHasAnyPermission([
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any,
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any,
    'patientmr:phi:careplan:read' as any,
    'patientmr:phi:careplan:readall' as any,
    'patientmr:phi:charting:read' as any,
    'patientmr:phi:charting:readall' as any
  ]);
  // PAT-09: the patient documents library has its own permission family
  const canReadDocuments = useHasAnyPermission([
    'patientmr:phi:documents:read' as any,
    'patientmr:phi:documents:readall' as any
  ]);

  // Check if user has permission to read Case Management (at least one of the four modules)
  const canReadCaseManagement = useHasAnyPermission([
    'patientmr:phi:authorization:read' as any,
    'patientmr:phi:authorization:readall' as any,
    'patientmr:phi:insurance:read' as any,
    'patientmr:phi:insurance:readall' as any,
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any,
    'patientmr:phi:referral:read' as any,
    'patientmr:phi:referral:readall' as any,
    'patientmr:phi:form:read' as any,
    'patientmr:phi:form:readall' as any,
    'patientmr:phi:documents:read' as any,
    'patientmr:phi:documents:readall' as any,
    'admin:patient:familyportal:read' as PermissionString
  ]);

  // Check if user has permission to read visits
  const canReadVisit = useHasAnyPermission([
    'patientmr:phi:visit:read' as any,
    'patientmr:phi:visit:readall' as any
  ]);

  // Phase 9B: My Day is for clinicians who start visits
  const canStartVisits = useHasAnyPermission(['patientmr:phi:visit:create' as any]);
  // Phase 18U U16: supervisors and QA review charts across patients (same gate as the route)
  const canReviewCharts = useHasAnyPermission(['patientmr:phi:supervisor:approve' as any]);

  // Check if user has permission to read schedule
  const canReadSchedule = useHasAnyPermission([
    'scheduling:phi:read' as any,
    'scheduling:phi:readall' as any,
    'scheduling:phi:get' as any,
    'scheduling:phi:getall' as any
  ]);

  // State for category expansions - collapsed by default
  const [expandedCategories, setExpandedCategories] = React.useState({
    clinicalCare: false,
    patientProfile: false,
    management: false
  });
  const [missedPromptCount, setMissedPromptCount] = React.useState(0);

  // Seed the badge from the server on mount so it's accurate even if the
  // EVV Reconciliation page hasn't been opened this session; live updates
  // from that page (via the custom event) take over afterwards.
  React.useEffect(() => {
    if (!canReadEvv) return;
    let cancelled = false;
    import('../../services/evv/reconciliation.service').then(({ EvvReconciliationService }) => {
      EvvReconciliationService.getSummary()
        .then((summary) => {
          if (cancelled) return;
          setMissedPromptCount(summary.missedPromptCount);
        })
        .catch(() => {
          // Non-critical; badge simply stays at 0 until an event updates it
        });
    });
    return () => { cancelled = true; };
  }, [canReadEvv]);

  React.useEffect(() => {
    const handleCount = (event: Event) => {
      setMissedPromptCount(Number((event as CustomEvent<number>).detail) || 0);
    };
    window.addEventListener('evv-missed-prompt-count', handleCount);
    return () => window.removeEventListener('evv-missed-prompt-count', handleCount);
  }, []);

  // isExpanded drives all layout decisions:
  // - Mobile: open when not collapsed
  // - Desktop pinned: always expanded
  // - Desktop unpinned: expanded only when hovered
  const isExpanded = isMobile ? !sidebarCollapsed : (sidebarPinned || sidebarHovered);

  // Add useEffect to check for null user info and sign out if needed
  React.useEffect(() => {
    if (!currentUserEmail) {
      console.log('User email missing, signing out');
      handleSignOut();
    }
  }, [currentUserEmail, handleSignOut]);
  
  // Track previous sidebar state to handle transitions
  const prevCollapsedRef = React.useRef(sidebarCollapsed);
  
  // Effect to maintain the same category expansion state when toggling between collapsed and expanded sidebar
  React.useEffect(() => {
    // Only run this effect when sidebar collapse state changes
    if (prevCollapsedRef.current !== sidebarCollapsed) {
      // Update the ref to the new value
      prevCollapsedRef.current = sidebarCollapsed;
      
      // No need to modify expandedCategories here - we'll use the same state for both modes
    }
  }, [sidebarCollapsed]);

  // Patient menu categories with new hierarchical structure
  const patientMenuCategories = React.useMemo(() => {
    const categories = [
      {
        id: 'patientProfile',
        name: 'Patient Profile',
        icon: UserCheck,
        items: [
          ...(canReadDashboard ? [{ name: 'Dashboard', icon: Home, originalName: 'Overview' }] : []),
          ...(canReadVisit ? [{ name: 'Visit History', icon: History, originalName: 'Visit History' }] : []),
          ...(canReadDemographics ? [{ name: 'Demographics', icon: User, originalName: 'Demographics' }] : []),
          ...(canReadMedications ? [{ name: 'Medications', icon: Syringe, originalName: 'Medications' }] : []),
          ...(canReadProviders ? [{ name: 'Care Team', icon: Users, originalName: 'Care Team' }] : []),
          ...(canReadGrowthChart ? [{ name: 'Growth Tracking', icon: TrendingUp, originalName: 'Growth Tracking' }] : [])
        ]
      },
      ...(canReadClinicalCare ? [{
        id: 'clinicalCare',
        name: 'Clinical Care',
        icon: Stethoscope,
        items: [
          ...(canReadCharting ? [{ name: 'Charting', icon: ChartLine, originalName: 'Charting' }] : []),
          ...(canReadAssessment ? [{ name: 'Assessments', icon: FileText, originalName: 'Assessments' }] : []),
          ...(canReadAssessment && oasisEnabled ? [{ name: 'OASIS', icon: FileText, originalName: 'OASIS' }] : []),
          ...(canReadCareplan ? [{ name: 'Care Plan', icon: ClipboardPlus, originalName: 'Care Plan' }] : []),
          ...(canReadPoc ? [{ name: 'POC Certifications', icon: ClipboardList, originalName: 'POC Certifications' }] : []),
          ...(canReadPoc ? [{ name: 'Orders', icon: ClipboardSignature, originalName: 'Orders' }] : [])
        ]
      }] : []),
      ...(canReadCaseManagement ? [{
        id: 'management',
        name: 'Case Management',
        icon: NotebookTabs,
        items: [
          ...(canReadReferral ? [{ name: 'Referrals', icon: Send, originalName: 'Referrals' }] : []),
          ...(canReadInsurance ? [{ name: 'Insurance and Coverage', icon: Shield, originalName: 'Insurance and Coverage' }] : []),
          ...(canReadAuthorization ? [{ name: 'Prior Authorizations', icon: KeyRound, originalName: 'Prior Authorizations' }] : []),
          ...(canReadForm ? [{ name: 'Forms', icon: FileText, originalName: 'Forms' }] : []),
          ...(canReadDocuments ? [{ name: 'Documents', icon: FolderOpen, originalName: 'Documents' }] : []),
          ...(canReadFamilyPortal ? [{ name: 'Family Portal', icon: HeartHandshake, originalName: 'Family Portal' }] : [])
        ]
      }] : [])
    ];
    
    return categories;
  }, [canReadFamilyPortal, canReadAssessment, oasisEnabled, canReadCareplan, canReadCharting, canReadPoc, canReadClinicalCare, canReadReferral, canReadInsurance, canReadAuthorization, canReadForm, canReadCaseManagement, canReadDocuments, canReadDemographics, canReadMedications, canReadProviders, canReadGrowthChart, canReadDashboard, canReadVisit]);

  // Effect to auto-expand the category containing the active subsection
  React.useEffect(() => {
    if (activeSubsection && activeSection === 'Patients') {
      // Find which category contains the active subsection
      let categoryToExpand = '';
      
      patientMenuCategories.forEach(category => {
        const hasActiveItem = category.items.some(item => 
          (item.originalName || item.name) === activeSubsection
        );
        if (hasActiveItem) {
          categoryToExpand = category.id;
        }
      });
      
      if (categoryToExpand) {
        setExpandedCategories(prev => ({
          ...prev,
          [categoryToExpand]: true
        }));
      }
    }
  }, [activeSubsection, activeSection, patientMenuCategories]);

  // Enhanced navigation handler for mobile
  const handleNavigation = (callback: () => void, shouldCloseSidebar: boolean = true) => {
    // Execute the navigation callback
    callback();
    
    // Close sidebar in mobile mode after a small delay to ensure state is set
    if (isMobile && shouldCloseSidebar) {
      setTimeout(() => {
        toggleSidebar();
      }, 50);
    }
  };

  // Function to get patient initials
  const getInitials = (name: string, firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    
    if (!name) return 'NA';
    
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  // Helper to convert Patient to PatientData
  const patientToPatientData = (patient: any) => ({
    id: patient.id.toString(),
    tenantId: patient.tenantId || '',
    mrn: patient.mrn,
    firstName: patient.firstName || (patient.name ? patient.name.split(' ')[0] : ''),
    lastName: patient.lastName || (patient.name ? patient.name.split(' ').slice(1).join(' ') : ''),
    dob: patient.dob,
    email: patient.email || '',
    gender: patient.gender || '',
    phoneNumber: patient.phoneNumber || '',
    status: patient.status === 'ACTIVE' || patient.status === 'INACTIVE' || patient.status === 'ON_HOLD' ? patient.status : (patient.status === 'Active' ? 'ACTIVE' : 'INACTIVE'),
  });

  // Toggle category expansion - in expanded sidebar close other categories when one is opened
  // In collapsed sidebar, just toggle the selected category
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      if (!isExpanded) {
        // In collapsed mode, just toggle the clicked category without affecting others
        return {
          ...prev,
          [category]: !prev[category]
        };
      } else {
        // In expanded mode, close other categories when opening a new one
        const newState = {
          clinicalCare: false,
          patientProfile: false,
          management: false
        };
        
        // Only toggle this category if it's not already open
        newState[category] = !prev[category];
        
        return newState;
      }
    });
  };
  
  // Reset all category expansions
  const resetCategoryExpansions = (defaultToPatientProfile = false) => {
    setExpandedCategories({
      clinicalCare: false,
      patientProfile: defaultToPatientProfile,
      management: false
    });
  };

  return (
    <>
      {/* Overlay backdrop for mobile */}
      {isMobile && (
        <div 
          className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${sidebarCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-50'}`}
          onClick={toggleSidebar}
          aria-hidden="true"
        ></div>
      )}
      {/* Desktop hover trigger strip — invisible zone at left edge, only shown when sidebar is hidden (unpinned) */}
      {!isMobile && !sidebarPinned && !sidebarHovered && (
        <div
          className="fixed left-0 top-0 h-screen w-5 z-[34]"
          onMouseEnter={() => setSidebarHovered(true)}
        />
      )}

      <aside
        className={`
          bg-slate-900 text-white flex flex-col
          transition-all duration-200 ease-in-out
          ${isMobile
            ? `fixed inset-0 w-full h-screen shadow-xl transform flex-shrink-0 ${sidebarCollapsed ? '-translate-x-full z-20' : 'translate-x-0 z-50'}`
            : sidebarPinned
              ? 'w-64 border-r border-slate-800 h-screen flex-shrink-0'
              : `fixed left-0 top-0 h-screen z-[35] border-r border-slate-800 w-64
                 ${isExpanded ? 'translate-x-0' : '-translate-x-full'}`
          }
        `}
        onMouseEnter={!isMobile ? () => setSidebarHovered(true) : undefined}
        onMouseLeave={!isMobile && !sidebarPinned ? () => setSidebarHovered(false) : undefined}
      >
      {/* Pin/unpin button for desktop */}
      {!isMobile && (
        <div className="flex items-center justify-end p-4 border-b border-slate-700">
          <button
            onClick={toggleSidebarPin}
            className="p-1 rounded-full hover:bg-slate-700 text-gray-300 hover:text-white flex-shrink-0"
            aria-label={sidebarPinned ? "Unpin sidebar" : "Pin sidebar open"}
            title={sidebarPinned ? "Unpin sidebar" : "Pin sidebar open"}
          >
            {sidebarPinned ? <ChevronsLeft size={18} /> : <ChevronsRight size={18} />}
          </button>
        </div>
      )}

      {/* Mobile-only header with logo, company name, and burger menu */}
      {isMobile && !sidebarCollapsed && (
        <div className="h-16 flex items-center justify-between px-2 py-2 border-b border-slate-700">
          <button 
            onClick={toggleSidebar}
            className="p-2 text-white hover:text-gray-300"
            aria-label="Close menu"
          >
            <Menu size={26} />
          </button>
          <div className="flex items-center justify-end ml-auto">
            <img src={iconWhite} alt="Pelora Health" className="h-6 w-6" />
            <span className="text-white text-lg md:text-2xl font-semibold ml-1">Pelora Health</span>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {/* Patients Section */}
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-b border-slate-800`}>
          <div
            className={`flex items-center justify-between ${isMobile ? 'mb-4 py-1' : 'mb-3'} cursor-pointer`}
            onClick={() => {
              toggleSection('Patients');
              // Reset category expansions when toggling the Patients section
              if (expandedSections['Patients']) {
                resetCategoryExpansions();
              }
              // In mobile mode, don't close the sidebar when clicking on Patient Care
              // This allows users to just expand the section without navigating
            }}
          >
            <div className="flex items-center">
              <Users className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`} size={isMobile ? 24 : 20} />
              {isExpanded && <span className={`font-medium text-gray-200 ${isMobile ? 'text-lg' : ''}`}>Patient Care</span>}
            </div>
            {isExpanded && (
              <ChevronDown
                size={isMobile ? 20 : 16}
                className={`transition-transform ${expandedSections['Patients'] ? 'rotate-180' : ''} text-gray-400`}
              />
            )}
          </div>

          {/* Patients Content */}
          {expandedSections['Patients'] && (
            <>
              {/* Home option */}
              {isExpanded && (
                <div
                  className={menuStyles.subMenuItem(
                    !isExpanded,
                    activeSubsection === null && activeSection === 'Patients',
                    isMobile
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(() => {
                      setActiveSection('Patients');
                      setActiveSubsection(null);
                      resetCategoryExpansions();
                    });
                  }}
                >
                  <Home size={18} className={menuStyles.iconStyle(
                    !isExpanded,
                    activeSubsection === null && activeSection === 'Patients'
                  )} />
                  <span>Home</span>
                </div>
              )}

              {/* Patient List */}
              {isExpanded && (
                <div className="space-y-1 mb-3">
                  <div 
                    className="py-2 px-2 rounded-lg cursor-pointer flex items-center justify-between hover:bg-slate-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMyPatientsExpanded(!myPatientsExpanded);
                    }}
                  >
                    <span className="font-medium text-gray-200">My Patients</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${myPatientsExpanded ? 'rotate-180' : ''} text-gray-400`}
                    />
                  </div>
                  {myPatientsExpanded && (
                    <div className="space-y-1 mb-3">
                      {myPatientsLoading ? (
                        <div className="py-2 px-3 text-sm text-gray-400">Loading patients...</div>
                      ) : myPatients && myPatients.length > 0 ? (
                        myPatients.slice(0, 3).map(patient => (
                          <div
                            key={patient.id}
                            className={`py-2 px-3 rounded-lg text-sm cursor-pointer flex items-center hover:bg-slate-700`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigation(() => {
                                if (selectedPatientId?.toString() === patient.id.toString()) {
                                  setSelectedPatient(null);
                                  setSelectedPatientId(null);
                                  setPatientUnselected(true);
                                  setActiveSection('Patients');
                                  setActiveSubsection(null);
                                  resetCategoryExpansions();
                                } else {
                                  setSelectedPatient(patientToPatientData(patient));
                                  setSelectedPatientId(patient.id.toString());
                                  // Set the active section to Patients and subsection to Overview (Dashboard)
                                  setActiveSection('Patients');
                                  setActiveSubsection('Overview');
                                  // Expand the Patient Profile category by default
                                  resetCategoryExpansions(true);
                                }
                              });
                            }}
                          >
                            <div className="flex-shrink-0 mr-2">
                              <PatientAvatar
                                patientId={patient.id.toString()}
                                name={`${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.name || 'Patient'}
                                profilePictureUrl={patient.profilePictureUrl}
                              />
                            </div>
                            <div className="truncate">
                              {patient.firstName && patient.lastName 
                                ? `${patient.firstName} ${patient.lastName}`
                                : patient.name || 'Unknown'}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-2 px-3 text-sm text-gray-400">No Patients Available</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Patient Information */}
              {selectedPatient && isExpanded && (
                <div className="mb-3 bg-slate-800 rounded-lg p-2.5 border border-slate-700">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center flex-grow overflow-hidden">
                      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs mr-2">
                        {getInitials(
                          `${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim()
                        )}
                      </div>
                      <div className="text-sm font-medium text-gray-200 truncate">
                        {selectedPatient.firstName && selectedPatient.lastName 
                          ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
                          : selectedPatient.mrn}
                      </div>
                    </div>
                    <button 
                      className="text-gray-400 hover:text-gray-200 flex-shrink-0 ml-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPatient(null);
                        setSelectedPatientId(null);
                        setPatientUnselected(true);
                        setActiveSection('Patients');
                        setActiveSubsection(null);
                        resetCategoryExpansions();
                      }}
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Selected Patient Icon - Collapsed */}
              {selectedPatient && !isExpanded && (
                <div className="mb-3 flex justify-center">
                  <div className="relative">
                    <div className="bg-slate-700 rounded-full p-2.5">
                      <User size={22} className="text-white" />
                    </div>
                    <button 
                      className="absolute -top-1 -right-1 bg-slate-600 rounded-full p-0.5 border border-slate-700 text-red-400 hover:text-red-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPatient(null);
                        setSelectedPatientId(null);
                        setPatientUnselected(true);
                        setActiveSection('Patients');
                        setActiveSubsection(null);
                        resetCategoryExpansions();
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* New Hierarchical Patient Menu Categories */}
              {selectedPatient && (
                <div className={`space-y-2 ${!isExpanded ? 'px-2' : ''}`}>
                  {patientMenuCategories.map((category) => (
                    <div key={category.id}>
                      {/* Category Header */}
                      {isExpanded ? (
                        <div
                          className={menuStyles.categoryHeader(!isExpanded, expandedCategories[category.id])}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category.id);
                          }}
                        >
                          <div className="flex items-center">
                            <category.icon size={18} className="mr-3 text-gray-300" />
                            <span className="font-medium text-gray-200">{category.name}</span>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`transition-transform ${expandedCategories[category.id] ? 'rotate-180' : ''} text-gray-400`}
                          />
                        </div>
                      ) : (
                        // Collapsed sidebar shows category icon with tooltip
                        <div
                          className={`flex justify-center mb-2 p-2 rounded-lg hover:bg-slate-700 ${
                            expandedCategories[category.id] ? 'bg-slate-700' : ''
                          } relative`}
                          title={category.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category.id);
                          }}
                        >
                          <category.icon size={18} className="text-gray-300" />
                          {expandedCategories[category.id] && (
                            <div className="absolute -right-1 -bottom-1 w-2 h-2 bg-slate-500 rounded-full"></div>
                          )}
                        </div>
                      )}

                      {/* Category Items */}
                      {expandedCategories[category.id] ? (
                        <div className={`space-y-1 ${!isExpanded ? '' : 'mb-3'}`}>
                          {category.items.map((item) => {
                            const itemName = item.originalName || item.name;
                            return (
                              <div
                                key={item.name}
                                className={menuStyles.categorySubItem(
                                  !isExpanded,
                                  activeSubsection === itemName,
                                  isMobile
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigation(() => {
                                    setActiveSection('Patients');
                                    setActiveSubsection(itemName);
                                    scrollToSection(itemName);

                                    // Ensure this item's parent category is expanded
                                    setExpandedCategories(prev => ({
                                      clinicalCare: false,
                                      patientProfile: false,
                                      management: false,
                                      [category.id]: true
                                    }));
                                  });
                                }}
                                title={item.name}
                              >
                                <item.icon
                                  size={16}
                                  className={`${!isExpanded ? '' : 'mr-2'} ${
                                    activeSubsection === itemName ? 'text-white' : 'text-gray-400'
                                  }`}
                                />
                                {isExpanded && <span>{item.name}</span>}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* My Day (Phase 9B): the clinician's current and upcoming shifts */}
        {canReadSchedule && canStartVisits && (
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-t border-slate-800`}>
          <div
            className={menuStyles.sectionHeader(!isExpanded)}
            onClick={() => handleNavigation(() => { setActiveSection('MyDay'); setActiveSubsection(null); })}
            title="My Day"
          >
            <div className="flex items-center">
              <CalendarCheck className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`} size={isMobile ? 24 : 20} />
              {isExpanded && <span className={`font-medium ${activeSection === 'MyDay' ? 'text-white' : 'text-gray-200'} ${isMobile ? 'text-lg' : ''}`}>My Day</span>}
            </div>
          </div>
        </div>
        )}

        {/* Phase 18U U16: charts awaiting review */}
        {canReviewCharts && (
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-t border-slate-800`}>
          <div
            className={menuStyles.sectionHeader(!isExpanded)}
            onClick={() => handleNavigation(() => { setActiveSection('ChartReview'); setActiveSubsection(null); })}
            title="Chart review"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavigation(() => { setActiveSection('ChartReview'); setActiveSubsection(null); }); } }}
          >
            <div className="flex items-center">
              <ClipboardCheck className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`} size={isMobile ? 24 : 20} />
              {isExpanded && <span className={`font-medium ${activeSection === 'ChartReview' ? 'text-white' : 'text-gray-200'} ${isMobile ? 'text-lg' : ''}`}>Chart review</span>}
            </div>
          </div>
        </div>
        )}

        {/* Schedule Section */}
        {canReadSchedule && (
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-t border-slate-800`}>
          <div
            className={menuStyles.sectionHeader(!isExpanded)}
            onClick={() => {
              toggleSection('Schedule');
              if (!isMobile) {
                setActiveSection('Schedule');
              }
            }}
          >
            <div className="flex items-center">
              <CalendarDays
                className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`}
                size={isMobile ? 24 : 20}
              />
              {isExpanded && <span className={`font-medium text-gray-200 ${isMobile ? 'text-lg' : ''}`}>Schedule</span>}
            </div>
            {isExpanded && (
              <ChevronDown
                size={isMobile ? 20 : 16}
                className={`transition-transform ${expandedSections['Schedule'] ? 'rotate-180' : ''} text-gray-400`}
              />
            )}
          </div>

          {expandedSections['Schedule'] && (
            <div className={menuStyles.submenuContainer(!isExpanded, isMobile)}>
              <div
                className={menuStyles.subMenuItem(!isExpanded, activeSubsection === 'Today', isMobile)}
                onClick={() => {
                  handleNavigation(() => {
                    setActiveSection('Schedule');
                    setActiveSubsection('Today');
                  });
                }}
                title="Today"
              >
                <Clock size={18} className={menuStyles.iconStyle(!isExpanded, activeSubsection === 'Today')} />
                {isExpanded && <span>Today</span>}
              </div>

              <div
                className={menuStyles.subMenuItem(!isExpanded, activeSubsection === 'Week', isMobile)}
                onClick={() => {
                  handleNavigation(() => {
                    setActiveSection('Schedule');
                    setActiveSubsection('Week');
                  });
                }}
                title="Week"
              >
                <Calendar size={18} className={menuStyles.iconStyle(!isExpanded, activeSubsection === 'Week')} />
                {isExpanded && <span>Week</span>}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Billing Section - claims (billing:claims:read) and/or EVV reconciliation (evv:reconciliation:read) */}
        {(canReadBilling || canReadEvv) && (
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-t border-slate-800`}>
          <div
            className={menuStyles.sectionHeader(!isExpanded)}
            onClick={() => {
              toggleSection('Billing');
              setActiveSection('Billing');
              setActiveSubsection(null);
            }}
          >
            <div className="flex items-center">
              <Receipt
                className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`}
                size={isMobile ? 24 : 20}
              />
              {isExpanded && <span className={`font-medium text-gray-200 ${isMobile ? 'text-lg' : ''}`}>Billing</span>}
            </div>
            {isExpanded && (
              <ChevronDown
                size={isMobile ? 20 : 16}
                className={`transition-transform ${expandedSections['Billing'] ? 'rotate-180' : ''} text-gray-400`}
              />
            )}
          </div>

          {expandedSections['Billing'] && (
            <ul className={menuStyles.submenuContainer(!isExpanded, isMobile)}>
              {canReadBilling && (
              <li
                className={menuStyles.subMenuItem(
                  !isExpanded,
                  activeSection === 'Billing' && activeSubsection === 'Claims', isMobile
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(() => {
                    setActiveSection('Billing');
                    setActiveSubsection('Claims');
                  });
                }}
                title="Claims"
              >
                <FileText
                  size={16}
                  className={menuStyles.iconStyle(
                    !isExpanded,
                    activeSection === 'Billing' && activeSubsection === 'Claims'
                  )}
                />
                {isExpanded && <span>Claims</span>}
              </li>
              )}

              {canReadEvv && (
              <li
                className={menuStyles.subMenuItem(
                  !isExpanded,
                  activeSection === 'Billing' && activeSubsection === 'EVV Reconciliation', isMobile
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(() => {
                    setActiveSection('Billing');
                    setActiveSubsection('EVV Reconciliation');
                  });
                }}
                title="EVV Reconciliation"
              >
                <ClipboardList
                  size={18}
                  className={menuStyles.iconStyle(
                    !isExpanded,
                    activeSection === 'Billing' && activeSubsection === 'EVV Reconciliation'
                  )}
                />
                {isExpanded && <span className="flex items-center gap-2">EVV Reconciliation{missedPromptCount > 0 && <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{missedPromptCount}</span>}</span>}
              </li>
              )}

              {billingReportsEnabled && (
              <li
                className={menuStyles.subMenuItem(
                  !isExpanded,
                  activeSection === 'Billing' && activeSubsection === 'Reports', isMobile
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(() => {
                    setActiveSection('Billing');
                    setActiveSubsection('Reports');
                  });
                }}
                title="Reports"
              >
                <PieChart
                  size={18}
                  className={menuStyles.iconStyle(
                    !isExpanded,
                    activeSection === 'Billing' && activeSubsection === 'Reports'
                  )}
                />
                {isExpanded && <span>Reports</span>}
              </li>
              )}
            </ul>
          )}
        </div>
        )}

        {/* Administration Section */}
        {(canReadUsers || canReadAgencies || canReadConfig || canReadPatients || canManageNotifications) && (
        <div className={`${!isExpanded && !isMobile ? 'px-2 py-4' : 'p-4'} border-t border-slate-800`}>
          <div
            className={menuStyles.sectionHeader(!isExpanded)}
            onClick={() => {
              toggleSection('Administration');
              if (!isMobile) {
                setActiveSection('Administration');
              }
            }}
          >
            <div className="flex items-center">
              <Settings
                className={`${!isExpanded ? 'mx-auto' : 'mr-3'} text-gray-300`}
                size={isMobile ? 24 : 20}
              />
              {isExpanded && <span className={`font-medium text-gray-200 ${isMobile ? 'text-lg' : ''}`}>Administration</span>}
            </div>
            {isExpanded && (
              <ChevronDown
                size={isMobile ? 20 : 16}
                className={`transition-transform ${expandedSections['Administration'] ? 'rotate-180' : ''} text-gray-400`}
              />
            )}
          </div>

          {expandedSections['Administration'] && (
            <ul className={menuStyles.submenuContainer(!isExpanded, isMobile)}>
              {canReadUsers && (
              <li
                className={menuStyles.subMenuItem(
                  !isExpanded,
                  activeAdminTab === 'userMgmt' && activeSection === 'Administration', isMobile
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(() => {
                    setActiveSection('Administration');
                    setActiveAdminTab('userMgmt');
                  });
                }}
                title="Users"
              >
                <Users
                  size={18}
                  className={menuStyles.iconStyle(
                    !isExpanded,
                    activeAdminTab === 'userMgmt' && activeSection === 'Administration'
                  )}
                />
                {isExpanded && <span>Users</span>}
              </li>
              )}

              {canReadPatients && (
              <li
                className={menuStyles.subMenuItem(
                  !isExpanded,
                  activeAdminTab === 'patientAdmin' && activeSection === 'Administration', isMobile
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(() => {
                    setActiveSection('Administration');
                    setActiveAdminTab('patientAdmin');
                  });
                }}
                title="Patients"
              >
                <Activity
                  size={18}
                  className={menuStyles.iconStyle(
                    !isExpanded,
                    activeAdminTab === 'patientAdmin' && activeSection === 'Administration'
                  )}
                />
                {isExpanded && <span>Patients</span>}
              </li>
              )}

              {canReadAgencies && (
                <li
                  className={menuStyles.subMenuItem(
                    !isExpanded,
                    activeAdminTab === 'agencySetup' && activeSection === 'Administration', isMobile
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(() => {
                      setActiveSection('Administration');
                      setActiveAdminTab('agencySetup');
                    });
                  }}
                  title="Agency Setup"
                >
                  <Building
                    size={18}
                    className={menuStyles.iconStyle(
                      !isExpanded,
                      activeAdminTab === 'agencySetup' && activeSection === 'Administration'
                    )}
                  />
                  {isExpanded && <span>Agency Setup</span>}
                </li>
              )}

              {canReadConfig && (
                <li
                  className={menuStyles.subMenuItem(
                    !isExpanded,
                    activeAdminTab === 'systemConfig' && activeSection === 'Administration', isMobile
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(() => {
                      setActiveSection('Administration');
                      setActiveAdminTab('systemConfig');
                    });
                  }}
                  title="System Config"
                >
                  <Settings
                    size={18}
                    className={menuStyles.iconStyle(
                      !isExpanded,
                      activeAdminTab === 'systemConfig' && activeSection === 'Administration'
                    )}
                  />
                  {isExpanded && <span>System Config</span>}
                </li>
              )}

              {canReadConfig && (
                <li
                  className={menuStyles.subMenuItem(
                    !isExpanded,
                    activeAdminTab === 'organizationAccount' && activeSection === 'Administration', isMobile
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(() => {
                      setActiveSection('Administration');
                      setActiveAdminTab('organizationAccount');
                    });
                  }}
                  title="Org Account"
                >
                  <CreditCard
                    size={18}
                    className={menuStyles.iconStyle(
                      !isExpanded,
                      activeAdminTab === 'organizationAccount' && activeSection === 'Administration'
                    )}
                  />
                  {isExpanded && <span>Org Account</span>}
                </li>
              )}

              {/* Phase 16F (REQ-NTF-06): notification routing settings */}
              {canManageNotifications && (
                <li
                  className={menuStyles.subMenuItem(
                    !isExpanded,
                    activeAdminTab === 'notificationSettings' && activeSection === 'Administration', isMobile
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(() => {
                      setActiveSection('Administration');
                      setActiveAdminTab('notificationSettings');
                    });
                  }}
                  title="Notifications"
                >
                  <BellRing
                    size={18}
                    className={menuStyles.iconStyle(
                      !isExpanded,
                      activeAdminTab === 'notificationSettings' && activeSection === 'Administration'
                    )}
                  />
                  {isExpanded && <span>Notifications</span>}
                </li>
              )}
            </ul>
          )}
        </div>
        )}

      </div>

      {/* Phase 14 SUP-04/05: help, support and security contact (no PHI) */}
      <div className={`border-t border-slate-700 ${!isExpanded ? 'py-3 px-2' : 'px-0 py-2'}`}>
        <SupportLinks compact={!isExpanded} />
      </div>

      {/* User profile and sign out section */}
      <div className={`border-t border-slate-700 ${!isExpanded ? 'py-4 px-2' : 'p-4'}`}>
        {!isExpanded ? (
          <div className="flex flex-col items-center space-y-4">
            <UserProfileBadge
              userId={userId}
              firstName={firstName}
              lastName={lastName}
              currentImageUrl={userProfilePic}
              onImageUpdate={onProfilePictureUpdate}
              size="medium"
              clickable={true}
            />
            <button 
              onClick={handleSignOut}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 transition-colors"
              title="Sign Out"
            >
              <LogOut size={20} className="text-gray-300" />
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center">
              <div className="mr-3 flex-shrink-0">
                <UserProfileBadge
                  userId={userId}
                  firstName={firstName}
                  lastName={lastName}
                  currentImageUrl={userProfilePic}
                  onImageUpdate={onProfilePictureUpdate}
                  size="medium"
                  clickable={true}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-medium text-gray-200 text-left truncate">
                  {firstName && lastName ? `${firstName} ${lastName}` : 'Unknown User'}
                </p>
                <p className="text-sm text-gray-400 text-left break-words">
                  {currentUserEmail || 'No email available'}
                </p>
              </div>
            </div>
            <button 
              onClick={handleSignOut}
              className="mt-4 flex w-full items-center px-4 py-3 text-base text-gray-200 hover:bg-slate-700 rounded-lg"
            >
              <LogOut size={20} className="mr-2" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
};

export default Sidebar;